import React, { useState, useEffect } from "react";
import {
  Loader2,
  Pencil,
  Check,
  X,
  Mail,
  Phone,
  ShieldCheck,
  ShieldAlert,
  ShieldQuestion,
  BadgeCheck,
  Building2,
  UserCircle,
  Lock,
  CalendarClock,
  LogIn,
} from "lucide-react";
import { Link } from "react-router-dom";
import { useAuth } from "src/contexts/AuthContext";
import { useToast } from "src/components/ui/toast";
import { Badge } from "src/components/ui/badge";
import { Button, buttonVariants } from "src/components/ui/button";
import { cn } from "src/utils/cn";
import { fetchProfile, updateProfile } from "src/apis/profile";

// ---------------------------------------------------------------------------
// Small presentational helpers
// ---------------------------------------------------------------------------

function Section({ icon: Icon, title, description, action, children }) {
  return (
    <section className="rounded-xl border border-slate-200 bg-white shadow-sm">
      <div className="flex items-start justify-between gap-4 border-b border-slate-100 px-5 py-4">
        <div className="flex items-start gap-3">
          {Icon && (
            <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-slate-50 text-slate-500">
              <Icon className="h-4 w-4" />
            </span>
          )}
          <div>
            <h3 className="text-[15px] font-semibold text-slate-900">{title}</h3>
            {description && (
              <p className="mt-0.5 text-[13px] text-slate-500">{description}</p>
            )}
          </div>
        </div>
        {action}
      </div>
      <div className="px-5 py-4">{children}</div>
    </section>
  );
}

// A read-only label/value row used across the read-mode sections.
function Field({ icon: Icon, label, children }) {
  return (
    <div className="flex flex-col gap-1 py-2.5 sm:flex-row sm:items-center sm:gap-4">
      <div className="flex w-44 shrink-0 items-center gap-2 text-[13px] font-medium text-slate-500">
        {Icon && <Icon className="h-4 w-4 text-slate-400" />}
        {label}
      </div>
      <div className="min-w-0 flex-1 text-[14px] text-slate-800">
        {children ?? <span className="text-slate-400">—</span>}
      </div>
    </div>
  );
}

function Avatar({ name }) {
  const initials = (name || "?")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase())
    .join("");
  return (
    <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full bg-[#4b5bed]/10 text-lg font-semibold text-[#4b5bed]">
      {initials || "?"}
    </div>
  );
}

function humanize(value) {
  if (!value) return null;
  return value
    .toString()
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatDateTime(value) {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  });
}

function formatDate(value) {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, { dateStyle: "medium" });
}

// RERA verification badge — colour + icon reflect the verification state.
function ReraStatusBadge({ status }) {
  const map = {
    verified: { variant: "success", icon: ShieldCheck, label: "Verified" },
    pending: { variant: "info", icon: ShieldQuestion, label: "Pending" },
    failed: { variant: "destructive", icon: ShieldAlert, label: "Failed" },
    unverified: { variant: "secondary", icon: ShieldQuestion, label: "Not verified" },
  };
  const { variant, icon: Icon, label } = map[status] || map.unverified;
  return (
    <Badge variant={variant} className="gap-1">
      <Icon className="h-3.5 w-3.5" />
      {label}
    </Badge>
  );
}

// ---------------------------------------------------------------------------
// Profile section (inline edit for name + phone)
// ---------------------------------------------------------------------------

function ProfileSection({ user, onSaved }) {
  const { toast } = useToast();
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: user.name || "", phone: user.phone || "" });

  // Reset the draft whenever we (re)enter edit mode or the user changes.
  useEffect(() => {
    if (!editing) setForm({ name: user.name || "", phone: user.phone || "" });
  }, [editing, user.name, user.phone]);

  const dirty = form.name !== (user.name || "") || form.phone !== (user.phone || "");

  async function handleSave(e) {
    e.preventDefault();

    if (!form.name.trim()) {
      toast({ variant: "destructive", title: "Validation Error", description: "Name cannot be blank." });
      return;
    }
    // Mirror the backend E.164 rule so the user gets immediate feedback.
    if (!/^\+[1-9]\d{1,14}$/.test(form.phone.trim())) {
      toast({
        variant: "destructive",
        title: "Invalid phone",
        description: "Use international format, e.g. +919876543210.",
      });
      return;
    }

    setSaving(true);
    try {
      const { data } = await updateProfile({ name: form.name.trim(), phone: form.phone.trim() });
      onSaved(data.profile);
      setEditing(false);
      toast({ title: "Profile updated", description: "Your information has been saved." });
    } catch (error) {
      const msg = error.response?.data?.errors?.join(", ") || "Failed to update your profile.";
      toast({ variant: "destructive", title: "Error", description: msg });
    } finally {
      setSaving(false);
    }
  }

  const inputClass =
    "h-10 w-full max-w-md rounded-lg border border-slate-200 bg-white px-3 text-[14px] text-slate-800 placeholder:text-slate-400 transition-colors focus:border-[#4b5bed] focus:outline-none focus:ring-2 focus:ring-[#4b5bed]/20 disabled:opacity-50";

  return (
    <Section
      icon={UserCircle}
      title="Profile"
      description="Your personal details as they appear across the workspace."
      action={
        !editing && (
          <Button variant="outline" size="sm" className="gap-1.5" onClick={() => setEditing(true)}>
            <Pencil className="h-3.5 w-3.5" />
            Edit
          </Button>
        )
      }
    >
      <div className="flex items-start gap-4">
        <Avatar name={form.name || user.name} />

        {editing ? (
          <form onSubmit={handleSave} className="flex-1 space-y-4">
            <div className="space-y-1.5">
              <label htmlFor="profile_name" className="text-[13px] font-semibold text-slate-700">
                Full name
              </label>
              <input
                id="profile_name"
                type="text"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="Your name"
                disabled={saving}
                className={inputClass}
                autoFocus
              />
            </div>

            <div className="space-y-1.5">
              <label htmlFor="profile_phone" className="text-[13px] font-semibold text-slate-700">
                Phone
              </label>
              <input
                id="profile_phone"
                type="tel"
                value={form.phone}
                onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                placeholder="+919876543210"
                disabled={saving}
                className={inputClass}
              />
              <p className="text-[11px] text-slate-400">Include the country code, e.g. +91.</p>
            </div>

            {/* Email is a login credential and can't be changed here. */}
            <div className="space-y-1.5">
              <label className="text-[13px] font-semibold text-slate-700">Email</label>
              <p className="text-[14px] text-slate-500">{user.email}</p>
            </div>

            <div className="flex items-center gap-2 pt-1">
              <Button type="submit" size="sm" className="gap-1.5" disabled={saving || !dirty}>
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                {saving ? "Saving" : "Save"}
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="gap-1.5"
                onClick={() => setEditing(false)}
                disabled={saving}
              >
                <X className="h-4 w-4" />
                Cancel
              </Button>
            </div>
          </form>
        ) : (
          <div className="flex-1 divide-y divide-slate-100">
            <Field label="Full name">
              <span className="font-medium text-slate-900">{user.name}</span>
            </Field>
            <Field icon={Mail} label="Email">
              {user.email}
            </Field>
            <Field icon={Phone} label="Phone">
              {user.phone ? (
                <span className="inline-flex items-center gap-2">
                  {user.phone}
                  {user.phone_verified_at ? (
                    <Badge variant="success" className="gap-1">
                      <BadgeCheck className="h-3.5 w-3.5" />
                      Verified
                    </Badge>
                  ) : (
                    <Badge variant="secondary">Unverified</Badge>
                  )}
                </span>
              ) : null}
            </Field>
          </div>
        )}
      </div>
    </Section>
  );
}

// ---------------------------------------------------------------------------
// Credentials (RERA)
// ---------------------------------------------------------------------------

function CredentialsSection({ rera }) {
  const hasRera = rera && (rera.number || rera.verification_status !== "unverified");

  return (
    <Section
      icon={ShieldCheck}
      title="Credentials"
      description="Your RERA registration and verification status."
      action={<ReraStatusBadge status={rera?.verification_status} />}
    >
      {hasRera ? (
        <div className="divide-y divide-slate-100">
          <Field label="RERA number">
            {rera.number ? <span className="font-medium text-slate-900">{rera.number}</span> : null}
          </Field>
          <Field label="Registered name">{rera.registered_name}</Field>
          <Field label="Registration type">{humanize(rera.registration_type)}</Field>
          <Field label="Expiry date">{formatDate(rera.expiry_date)}</Field>
          <Field label="Verified on">{formatDateTime(rera.verified_at)}</Field>
          {rera.verification_status === "failed" && rera.failure_reason && (
            <div className="py-2.5">
              <div className="flex items-start gap-2 rounded-lg border border-red-100 bg-red-50 px-3 py-2 text-[13px] text-red-700">
                <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" />
                <span>{rera.failure_reason}</span>
              </div>
            </div>
          )}
        </div>
      ) : (
        <p className="text-[14px] text-slate-500">
          No RERA registration is linked to your account.
        </p>
      )}
    </Section>
  );
}

// ---------------------------------------------------------------------------
// Role & organization
// ---------------------------------------------------------------------------

function RoleOrgSection({ membership, organization, organizations }) {
  const roleName = membership?.role?.name || humanize(membership?.role?.slug);

  return (
    <Section
      icon={Building2}
      title="Role & organization"
      description="Where you belong and what you can do."
    >
      <div className="divide-y divide-slate-100">
        <Field label="Role">
          {roleName ? <Badge variant="outline">{roleName}</Badge> : null}
        </Field>
        <Field label="Organization">
          {organization?.name ? (
            <span className="inline-flex items-center gap-2">
              {organization.logo_url ? (
                <img
                  src={organization.logo_url}
                  alt=""
                  className="h-5 w-5 rounded-full object-cover"
                />
              ) : (
                <Building2 className="h-4 w-4 text-slate-400" />
              )}
              <span className="font-medium text-slate-900">{organization.name}</span>
            </span>
          ) : null}
        </Field>
        {organizations && organizations.length > 1 && (
          <Field label="Member of">
            <div className="flex flex-wrap gap-1.5">
              {organizations.map((org) => (
                <Badge
                  key={org.id}
                  variant={org.id === organization?.id ? "info" : "secondary"}
                >
                  {org.name}
                </Badge>
              ))}
            </div>
          </Field>
        )}
      </div>
    </Section>
  );
}

// ---------------------------------------------------------------------------
// Account & security
// ---------------------------------------------------------------------------

function AccountSection({ user }) {
  return (
    <Section
      icon={Lock}
      title="Account & security"
      description="Account status, sign-in activity, and password."
    >
      <div className="divide-y divide-slate-100">
        <Field icon={CalendarClock} label="Member since">
          {formatDate(user.created_at)}
        </Field>
        <Field icon={LogIn} label="Last sign-in">
          {/* current_sign_in_at is "this session"; last_sign_in_at is the prior one. */}
          {formatDateTime(user.last_sign_in_at || user.current_sign_in_at)}
        </Field>
        <div className="flex flex-col gap-2 pt-3 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-[13px] text-slate-500">
            Keep your account secure by using a strong, unique password.
          </p>
          <Link
            to="/configurations/reset-password"
            className={cn(buttonVariants({ variant: "outline", size: "sm" }), "gap-1.5")}
          >
            <Lock className="h-3.5 w-3.5" />
            Change password
          </Link>
        </div>
      </div>
    </Section>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function MyInformationPage() {
  const { currentUser, currentMembership, organization, organizations, setCurrentUser } = useAuth();

  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const { data } = await fetchProfile();
        if (active) setProfile(data.profile);
      } catch (error) {
        console.error("Failed to load profile:", error);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  // Merge a saved profile back into state and keep the app-wide auth user in
  // sync so the sidebar/header reflect the new name immediately.
  function handleSaved(updated) {
    setProfile(updated);
    if (updated?.user) {
      setCurrentUser((prev) => ({ ...prev, name: updated.user.name, phone: updated.user.phone }));
    }
  }

  // Fall back to bootstrap auth data so the page renders instantly, then the
  // fetched profile (with richer fields) replaces it.
  const user = profile?.user || currentUser || {};
  const membership = profile?.current_membership || currentMembership;
  const org = profile?.organization || organization;
  const orgs = profile?.organizations || organizations;

  return (
    <div className="max-w-3xl space-y-6">
      <div>
        <h2 className="text-xl font-bold tracking-tight text-slate-900">My Information</h2>
        <p className="mt-1 text-sm text-slate-500">
          View and manage your personal details, credentials, and account.
        </p>
      </div>

      {loading && !profile ? (
        <div className="flex h-48 items-center justify-center rounded-xl border border-slate-200 bg-white shadow-sm">
          <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
        </div>
      ) : (
        <div className="space-y-6">
          <ProfileSection user={user} onSaved={handleSaved} />
          <CredentialsSection rera={user.rera} />
          <RoleOrgSection membership={membership} organization={org} organizations={orgs} />
          <AccountSection user={user} />
        </div>
      )}
    </div>
  );
}
